import { ArrowUDownLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowUDownLeft";
import { CaretUpDownIcon } from "@phosphor-icons/react/dist/csr/CaretUpDown";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { PlugsConnectedIcon } from "@phosphor-icons/react/dist/csr/PlugsConnected";
import { TerminalWindowIcon } from "@phosphor-icons/react/dist/csr/TerminalWindow";
import { UserCircleIcon } from "@phosphor-icons/react/dist/csr/UserCircle";
import { UserSwitchIcon } from "@phosphor-icons/react/dist/csr/UserSwitch";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { SidebarMenuButton, useSidebar } from "@volca/ui/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { useDevBarEligible } from "@/app-shell/dev-bar";
import {
  devBarPreference,
  useDevBarVisible,
} from "@/app-shell/dev-bar-preference";
import { ThemeMenu } from "@/app-shell/theme-menu";
import { useQuickbooksConnect } from "@/app-shell/use-quickbooks-connect";
import type { SettingsNav } from "@/app-shell/use-settings-nav";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { useImpersonation } from "@/core/impersonation";
import {
  WorkspaceBusinesses,
  WorkspaceLogo,
  WorkspaceRailTrigger,
} from "./workspace-businesses";

interface WorkspaceMenuProps {
  settings?: SettingsNav | null;
  presentation?: "sidebar" | "header" | "rail";
  onImpersonate?: () => void;
  onProfile?: () => void;
}

const WorkspaceMark = ({
  name,
  src,
}: {
  name: string | null;
  src?: string | null;
}) => (
  <>
    {name && <WorkspaceLogo name={name} src={src} />}
    {name === null ? (
      <span
        data-slot="skeleton"
        className="h-4 w-24 animate-pulse rounded-md bg-neutral-hovered"
      />
    ) : (
      <span className="min-w-0 flex-1 truncate text-left" title={name}>
        {name}
      </span>
    )}
  </>
);

export const WorkspaceMenu = ({
  settings,
  presentation = "sidebar",
  onImpersonate,
  onProfile,
}: WorkspaceMenuProps) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { selectedBusiness, isLoading: isBusinessLoading } = useBusiness();
  const { isMobile, setOpenMobile } = useSidebar();
  const quickbooks = useQuickbooksConnect();
  const { isImpersonating, endImpersonation, isSavePending } =
    useImpersonation();
  const devBarEligible = useDevBarEligible();
  const devBarVisible = useDevBarVisible();

  const workspaceName = isBusinessLoading
    ? null
    : (selectedBusiness?.name ?? "Volca");
  const closeMobileMenu = () => {
    if (isMobile) setOpenMobile(false);
  };
  const accountLabel = workspaceName ? `${workspaceName} account` : "Account";
  const menuSide =
    presentation === "header"
      ? "bottom"
      : presentation === "rail"
        ? "right"
        : "top";
  const menuAlign = presentation === "rail" ? "end" : "start";
  const trigger = (
    <DropdownMenuTrigger
      render={
        presentation === "rail" ? (
          <WorkspaceRailTrigger
            name={workspaceName}
            src={selectedBusiness?.logo_url}
            aria-label={accountLabel}
          />
        ) : presentation === "header" ? (
          <Button
            variant="ghost"
            aria-label={
              workspaceName
                ? `Switch organization, ${workspaceName}`
                : "Switch organization"
            }
            className="h-8 max-w-xs min-w-0 px-2 font-normal data-popup-open:bg-neutral-hovered"
          />
        ) : (
          <SidebarMenuButton
            tooltip={workspaceName ?? "Account"}
            aria-label={accountLabel}
            className="data-popup-open:bg-neutral-hovered"
          />
        )
      }
    >
      {presentation === "rail" ? null : (
        <>
          <WorkspaceMark
            name={workspaceName}
            src={selectedBusiness?.logo_url}
          />
          <CaretUpDownIcon
            className={cn({
              "shrink-0": presentation === "header",
              "ml-auto": presentation !== "header",
            })}
          />
        </>
      )}
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {presentation === "rail" ? (
        <Tooltip>
          <TooltipTrigger render={trigger} />
          <TooltipContent side="right">{accountLabel}</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <DropdownMenuContent
        side={menuSide}
        align={menuAlign}
        sideOffset={8}
        className="w-80 max-w-(--available-width) p-1.5"
      >
        {settings?.href && (
          <DropdownMenuItem
            render={<Link to={settings.href} />}
            onClick={() => {
              closeMobileMenu();
              settings.onNavigate();
            }}
          >
            <GearSixIcon />
            Settings
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => {
            closeMobileMenu();
            if (onProfile) onProfile();
            else void navigate("/profile");
          }}
        >
          <UserCircleIcon />
          Profile
        </DropdownMenuItem>
        <ThemeMenu />
        {devBarEligible && !isImpersonating && (
          <DropdownMenuItem
            onClick={() => devBarPreference.set(!devBarVisible)}
          >
            <TerminalWindowIcon />
            {devBarVisible ? "Hide developer bar" : "Show developer bar"}
          </DropdownMenuItem>
        )}
        {quickbooks.show && (
          <DropdownMenuItem
            disabled={quickbooks.isConnected || quickbooks.isPending}
            onClick={() => void quickbooks.connect()}
          >
            <PlugsConnectedIcon />
            {quickbooks.isConnected
              ? "QuickBooks connected"
              : "Connect QuickBooks"}
          </DropdownMenuItem>
        )}
        {onImpersonate && (
          <DropdownMenuItem onClick={onImpersonate}>
            <UserSwitchIcon />
            {isImpersonating ? "Switch impersonation" : "Impersonate"}
          </DropdownMenuItem>
        )}
        {isImpersonating && (
          <DropdownMenuItem
            disabled={isSavePending}
            onClick={() => {
              // The customer's page means nothing to the admin's account, so
              // stopping returns home, as the developer bar's Stop does.
              void endImpersonation()
                .then(() => navigate("/"))
                .catch((error) => {
                  toast.error("Couldn't stop impersonating. Try again.", {
                    description:
                      error instanceof Error ? error.message : undefined,
                  });
                });
            }}
          >
            <ArrowUDownLeftIcon />
            Stop impersonation
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <WorkspaceBusinesses onSelect={closeMobileMenu} />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}>Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
